/**
 * The panel itself: a drag strip, the editor, the status bar, and Settings.
 * Everything stateful lives in `@/store/app-store`; App only owns whether
 * Settings is showing and the two keys that matter.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ConflictPane } from '@/components/ConflictPane'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SettingsDialog } from '@/components/SettingsDialog'
import { StatusBar } from '@/components/StatusBar'
import { MarkdownEditor, type MarkdownEditorHandle } from '@/editor/MarkdownEditor'
import {
  useNotes,
  usePlatform,
  useResolvedTheme,
  useSettings,
  useSync,
} from '@/store/app-store'

export function App(): ReactNode {
  const platform = usePlatform()
  const { settings } = useSettings()
  const theme = useResolvedTheme()
  const { body, setBody } = useNotes()
  const syncStatus = useSync()

  const [settingsOpen, setSettingsOpen] = useState(false)
  /**
   * Whether the Resolution pane is showing. Never true on its own: a Pending
   * Conflict shows as a badge and the pane opens from it, so quick capture is never
   * met with a decision (ADR-0002).
   */
  const [conflictOpen, setConflictOpen] = useState(false)
  const editorRef = useRef<MarkdownEditorHandle>(null)

  /**
   * Radix hands focus back to the trigger that opened the dialog, and this one
   * often has none — it opens from a keyboard shortcut. Focus would land on
   * <body> and the panel would stop responding to the keyboard until the user
   * clicked. Put the caret back where it belongs.
   */
  const closeSettings = useCallback(() => {
    setSettingsOpen(false)
    requestAnimationFrame(() => editorRef.current?.focus())
  }, [])

  /** The same caret rescue, for the same reason: the pane opens from the status row. */
  const closeConflict = useCallback(() => {
    setConflictOpen(false)
    requestAnimationFrame(() => editorRef.current?.focus())
  }, [])

  /** Escape, from wherever it was pressed: peel one layer, or leave. */
  const dismiss = useCallback(() => {
    if (settingsOpen) {
      closeSettings()
      return
    }
    // Ahead of hiding the panel and behind Settings, which is the order they can be
    // stacked in: Settings opens over the pane, never the other way round.
    if (conflictOpen) {
      closeConflict()
      return
    }
    platform.window.hide()
  }, [closeConflict, closeSettings, conflictOpen, platform, settingsOpen])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Bubble phase, and skip anything already claimed, so that whatever is
      // closest to the user gets first refusal — CodeMirror's own bindings and,
      // above all, the shortcut recorder in Settings, which exists to swallow
      // raw keystrokes.
      if (event.defaultPrevented || event.isComposing) return

      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key === ',') {
        event.preventDefault()
        setSettingsOpen(true)
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dismiss])

  useEffect(() => {
    // `Platform` describes what both targets can do; being told "the panel just
    // appeared, put the caret back" is desktop-only, so it stays on the preload
    // bridge rather than forcing the web build to fake an event it never fires.
    const bridge = window.jot
    if (!bridge) return
    return bridge.onFocusEditor(() => editorRef.current?.focus())
  }, [])

  return (
    <div className="flex h-full flex-col overflow-hidden bg-canvas text-fg">
      <div className="drag-region h-[10px] shrink-0" aria-hidden="true" />

      <div className="no-drag min-h-0 flex-1 overflow-hidden">
        <MarkdownEditor
          ref={editorRef}
          value={body}
          onChange={setBody}
          fontSize={settings.fontSize}
          lineWrap={settings.lineWrap}
          spellCheck={settings.spellCheck}
          theme={theme}
          autoFocus
          // Routed through the editor as well as the window listener: its
          // binding stands down for CodeMirror's own panels and turns on tab
          // focus mode first, neither of which a bare listener can know about.
          onEscape={dismiss}
        />
      </div>

      {/* The row reports state that came from outside this device — a Jot
          Server's `updatedAt`, a note file's header — and it is the only place a
          persisted value reaches a formatter. Boundaried so that the worst such a
          value can do is cost the row: the editor above stays mounted, and
          Settings is still one Cmd/Ctrl+, away. */}
      <ErrorBoundary fallback={<footer className="h-7 shrink-0 border-t bg-surface" />}>
        <StatusBar
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenConflict={() => setConflictOpen(true)}
        />
      </ErrorBoundary>

      {/* Mounted only while there is one to resolve, so the pane cannot outlive the
          Conflict it is about: a Resolution that lands from anywhere — this pane, or a
          pass that found the record gone — takes the status out of `conflict` and the
          pane with it. */}
      {syncStatus.kind === 'conflict' ? (
        <ConflictPane
          open={conflictOpen}
          onOpenChange={(next) => (next ? setConflictOpen(true) : closeConflict())}
          record={syncStatus.record}
          localBody={body}
        />
      ) : null}

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={(next) => (next ? setSettingsOpen(true) : closeSettings())}
      />
    </div>
  )
}
